import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Upload, Download, Loader2, Trash2, Undo2, PenTool,
    Square as SquareIcon, Check,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

/**
 * Omlijnen & Blurren tool.
 *  - User uploads a photo (or starts from a delivered project photo).
 *  - Picks a MODE: "line" (polyline outline) or "blur" (polygon zone).
 *  - Clicks points on the canvas to add vertices.
 *  - Lines: customizable HEX color + thickness slider.
 *  - Blur: intensity percentage 1..100.
 *  - On "Download" the backend renders at NATIVE resolution and returns a
 *    crisp JPG. Costs 1 credit (admins free).
 *
 * All client coordinates are stored in the photo's INTRINSIC pixel space
 * (image.naturalWidth × naturalHeight), so the server can render exactly
 * what the user drew regardless of viewport size or DPR.
 */
export default function AdminToolOutlineBlur() {
    const { t } = useTranslation();
    const [sourceUrl, setSourceUrl] = useState(null);
    const [sourceDims, setSourceDims] = useState({ w: 0, h: 0 });
    const [uploading, setUploading] = useState(false);
    const [balance, setBalance] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);

    // Drawing state
    const [mode, setMode] = useState("line"); // "line" | "blur"
    const [lineColor, setLineColor] = useState("#E5A93D");
    const [lineThickness, setLineThickness] = useState(8);
    const [blurPercent, setBlurPercent] = useState(60);
    const [operations, setOperations] = useState([]); // completed ops
    const [draftPoints, setDraftPoints] = useState([]); // current shape's vertices

    const [submitting, setSubmitting] = useState(false);
    const [resultUrl, setResultUrl] = useState(null);

    const fileInputRef = useRef(null);
    const imgRef = useRef(null);
    const canvasRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);

    /* ---------- Global guard: prevent the browser from opening the file
        if the user drops it OUTSIDE the upload zone (default behaviour
        replaces the page with the file URL). ---------------------------- */
    useEffect(() => {
        const preventOpen = (e) => { e.preventDefault(); };
        window.addEventListener("dragover", preventOpen);
        window.addEventListener("drop", preventOpen);
        return () => {
            window.removeEventListener("dragover", preventOpen);
            window.removeEventListener("drop", preventOpen);
        };
    }, []);

    /* ---------- Load credit state ----------------------------------- */
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/tools/me");
                setBalance(Number(data?.credits_balance || 0));
                setIsAdmin(!!data?.is_admin);
            } catch (e) { /* noop */ }
        })();
    }, []);

    /* ---------- Upload helper --------------------------------------- */
    const uploadFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error(t("tools.common.images_only"));
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            // Reset drawing canvas
            setOperations([]);
            setDraftPoints([]);
            setResultUrl(null);
            setSourceUrl(data.url);
        } catch (err) {
            toast.error(t("tools.common.upload_failed"));
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onPickFile = (e) => uploadFile(e.target.files?.[0]);

    const onDropFile = (e) => {
        e.preventDefault();
        setIsDragging(false);
        uploadFile(e.dataTransfer?.files?.[0]);
    };

    const onDragOver = (e) => {
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
    };

    const onDragLeave = (e) => {
        // Only clear if the pointer actually leaves the drop-zone (relatedTarget
        // is null or outside the zone).
        if (!e.currentTarget.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    };

    /* ---------- Image -> capture intrinsic dimensions --------------- */
    const onImgLoad = () => {
        const el = imgRef.current;
        if (!el) return;
        setSourceDims({ w: el.naturalWidth || el.width, h: el.naturalHeight || el.height });
    };

    /* ---------- Canvas click -> add vertex in INTRINSIC coords ------ */
    const onCanvasClick = (e) => {
        if (!imgRef.current || !sourceDims.w) return;
        const rect = imgRef.current.getBoundingClientRect();
        const ratioX = sourceDims.w / rect.width;
        const ratioY = sourceDims.h / rect.height;
        const x = (e.clientX - rect.left) * ratioX;
        const y = (e.clientY - rect.top) * ratioY;
        setDraftPoints((p) => [...p, [x, y]]);
    };

    /* ---------- Finish drawing the current shape -------------------- */
    const finishCurrent = useCallback(() => {
        if (draftPoints.length < 2) {
            toast.warning(mode === "line" ? "Tip: klik minstens 2 punten." : "Tip: blur-zones hebben minstens 3 punten nodig.");
            return;
        }
        if (mode === "blur" && draftPoints.length < 3) {
            toast.warning(t("tools.outline_blur.min_points"));
            return;
        }
        const op = mode === "line"
            ? { type: "line", points: draftPoints, color: lineColor, thickness: lineThickness }
            : { type: "blur", points: draftPoints, blur_percent: blurPercent };
        setOperations((o) => [...o, op]);
        setDraftPoints([]);
    }, [draftPoints, mode, lineColor, lineThickness, blurPercent]);

    /* ---------- Undo last action ------------------------------------ */
    const undo = () => {
        if (draftPoints.length > 0) {
            setDraftPoints((p) => p.slice(0, -1));
        } else if (operations.length > 0) {
            setOperations((o) => o.slice(0, -1));
        }
    };

    /* ---------- Clear everything ------------------------------------ */
    const clearAll = () => {
        setDraftPoints([]);
        setOperations([]);
    };

    /* ---------- Canvas sizing — runs only on image-load / resize. Keeps
        the dpr-scaled backing buffer in sync with the visible image. The
        DRAW effect below intentionally does NOT mutate canvas dimensions
        (which would clear the buffer on every state change) — instead it
        resets the transform with setTransform() so the same canvas size
        is reused across renders. This prevents the photo/lines from
        flickering or disappearing when the user changes colour/thickness
        rapidly via the color picker. */
    const canvasSizeRef = useRef({ w: 0, h: 0 });

    useEffect(() => {
        if (!canvasRef.current || !imgRef.current || !sourceDims.w) return undefined;
        const resize = () => {
            const c = canvasRef.current;
            const img = imgRef.current;
            if (!c || !img) return;
            const rect = img.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;
            const dpr = window.devicePixelRatio || 1;
            const w = Math.round(rect.width * dpr);
            const h = Math.round(rect.height * dpr);
            if (c.width !== w || c.height !== h) {
                c.width = w;
                c.height = h;
                c.style.width = `${rect.width}px`;
                c.style.height = `${rect.height}px`;
                canvasSizeRef.current = { w: rect.width, h: rect.height };
                // Trigger the draw effect by bumping a redraw counter.
                setRedrawTick((t) => t + 1);
            }
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(imgRef.current);
        window.addEventListener("resize", resize);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", resize);
        };
    }, [sourceDims]);

    /* ---------- Render preview on the canvas overlay ---------------- */
    const [redrawTick, setRedrawTick] = useState(0);
    useEffect(() => {
        if (!canvasRef.current || !imgRef.current || !sourceDims.w) return;
        const c = canvasRef.current;
        // Read CSS size from the cached size (set by the resize effect). If
        // the image hasn't been measured yet, fall back to getBoundingClientRect.
        let rect = canvasSizeRef.current;
        if (!rect.w || !rect.h) {
            const r = imgRef.current.getBoundingClientRect();
            if (r.width === 0 || r.height === 0) return;
            rect = { w: r.width, h: r.height };
        }
        const ratioX = rect.w / sourceDims.w;
        const ratioY = rect.h / sourceDims.h;
        const dpr = window.devicePixelRatio || 1;
        const ctx = c.getContext("2d");
        // Reset transform and clear — DO NOT touch c.width/c.height (that's
        // owned by the resize effect; mutating it here would wipe the
        // image's stacking context briefly on every render).
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, c.width, c.height);
        ctx.scale(dpr, dpr);

        const project = (pt) => [pt[0] * ratioX, pt[1] * ratioY];

        // Helper: render REAL blur preview inside a polygon. Uses canvas
        // ctx.filter (browser GPU-accelerated Gaussian) to draw the source
        // image clipped to the polygon. The blur radius in CSS-px is scaled
        // to match what the backend will produce at native resolution — so
        // the on-screen preview is visually identical to the exported JPG.
        // The same quadratic mapping is used server-side in
        // tools.py::_render_outline_blur_pil.
        const previewBlur = (pts, pct) => {
            if (!imgRef.current || !imgRef.current.complete || pts.length < 3) return;
            const p = Math.max(1, Math.min(100, Number(pct) || 50));
            // Source-pixel radius (matches backend formula)
            const radiusSource = Math.max(2, Math.round((p / 100) ** 2 * 80 + p * 0.4));
            // Scale to on-screen px so what you see == what you get.
            const radiusScreen = Math.max(1, radiusSource * ((ratioX + ratioY) / 2));
            ctx.save();
            ctx.beginPath();
            pts.forEach((pt, i) => {
                const [x, y] = project(pt);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            });
            ctx.closePath();
            ctx.clip();
            // Tiny inset offset prevents a visible 1-2px crisp seam at the
            // polygon edge on some browsers (the blur reaches outside the
            // clip region in shader space).
            ctx.filter = `blur(${radiusScreen}px)`;
            ctx.drawImage(imgRef.current, 0, 0, rect.w, rect.h);
            ctx.filter = "none";
            ctx.restore();
        };

        // Pass 1: render BLUR previews FIRST so line strokes are drawn on
        // top of them (matches the backend ordering).
        operations.forEach((op) => {
            if (op.type === "blur") previewBlur(op.points, op.blur_percent);
        });
        if (mode === "blur" && draftPoints.length >= 3) {
            previewBlur(draftPoints, blurPercent);
        }

        // Pass 2: subtle marker outline around each finished blur zone so
        // the admin can still grab/identify it (small dashed border, no
        // tinted overlay anymore — it would defeat the live preview).
        operations.forEach((op) => {
            if (op.type !== "blur") return;
            ctx.save();
            ctx.beginPath();
            op.points.forEach((pt, i) => {
                const [x, y] = project(pt);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            });
            ctx.closePath();
            ctx.strokeStyle = "rgba(52, 211, 153, 0.55)";
            ctx.lineWidth = 1.5;
            ctx.setLineDash([5, 4]);
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.restore();
        });

        // Pass 3: committed LINE strokes
        operations.forEach((op) => {
            if (op.type !== "line") return;
            ctx.beginPath();
            ctx.strokeStyle = op.color || "#E5A93D";
            ctx.lineWidth = (op.thickness || 8) * Math.min(ratioX, ratioY);
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            op.points.forEach((p, i) => {
                const [x, y] = project(p);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            });
            ctx.stroke();
        });

        // Draft shape preview
        if (draftPoints.length > 0) {
            ctx.strokeStyle = mode === "line" ? lineColor : "rgba(52, 211, 153, 0.85)";
            ctx.lineWidth = mode === "line" ? lineThickness * Math.min(ratioX, ratioY) : 2;
            ctx.lineCap = "round";
            ctx.lineJoin = "round";
            ctx.setLineDash(mode === "blur" ? [6, 4] : []);
            ctx.beginPath();
            draftPoints.forEach((p, i) => {
                const [x, y] = project(p);
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            });
            // No tinted fill for blur-mode anymore — the live blur preview
            // already shows what will be applied.
            ctx.stroke();
            ctx.setLineDash([]);
            // Vertex dots
            draftPoints.forEach((p) => {
                const [x, y] = project(p);
                ctx.beginPath();
                ctx.arc(x, y, 5, 0, Math.PI * 2);
                ctx.fillStyle = "#FFFFFF";
                ctx.fill();
                ctx.strokeStyle = "#FFFFFF";
                ctx.lineWidth = 1.5;
                ctx.stroke();
            });
        }
    }, [operations, draftPoints, mode, lineColor, lineThickness, blurPercent, sourceDims, redrawTick]);

    /* ---------- Submit to backend ----------------------------------- */
    const submit = async () => {
        // Auto-commit any pending draft
        const pending = draftPoints.length >= (mode === "blur" ? 3 : 2);
        let allOps = operations;
        if (pending) {
            const op = mode === "line"
                ? { type: "line", points: draftPoints, color: lineColor, thickness: lineThickness }
                : { type: "blur", points: draftPoints, blur_percent: blurPercent };
            allOps = [...operations, op];
        }
        if (allOps.length === 0) {
            toast.warning("Voeg minstens één lijn of blur-zone toe.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/tools/outline-blur/render", {
                source_url: sourceUrl,
                source_width: sourceDims.w,
                source_height: sourceDims.h,
                operations: allOps,
                jpeg_quality: 96,
            });
            setResultUrl(data.result_url);
            if (typeof data.credits_balance === "number") setBalance(data.credits_balance);
            toast.success("Afbeelding klaar — download starten.");
            // Auto-trigger the browser download
            const dl = document.createElement("a");
            dl.href = data.result_url;
            dl.download = `omlijnd-${Date.now()}.jpg`;
            document.body.appendChild(dl);
            dl.click();
            document.body.removeChild(dl);
            // Commit the auto-committed op so the canvas reflects what was
            // exported (and the user can keep editing on top of it).
            if (pending) {
                setOperations(allOps);
                setDraftPoints([]);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || t("tools.outline_blur.render_failed"));
        } finally {
            setSubmitting(false);
        }
    };

    /* ---------- Render ---------------------------------------------- */
    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="tool-outline-blur-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
                {/* Header */}
                <div className="flex items-center justify-between gap-6 mb-8">
                    <Link
                        to={isAdmin ? "/admin/tools" : "/tools"}
                        className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950"
                        data-testid="tool-back-link"
                    >
                        <ArrowLeft className="h-4 w-4" /> Tools
                    </Link>
                    <div className="text-xs uppercase tracking-wider text-zinc-500">
                        {isAdmin ? (
                            <span className="text-[#059669]">{t("tools.common.admin_free")}</span>
                        ) : (
                            <span>{t("tools.common.balance_label")} <span className="text-zinc-950 tabular-nums">{balance}</span> {t("tools.common.credits")}</span>
                        )}
                    </div>
                </div>

                <div className="mb-8">
                    <p className="text-xs uppercase tracking-[0.18em] text-[#059669] mb-3 inline-flex items-center gap-2">
                        <PenTool className="h-3.5 w-3.5" /> {t("tools.outline_blur.eyebrow")}
                    </p>
                    <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                        {t("tools.outline_blur.title")}
                    </h1>
                    <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
                        {t("tools.outline_blur.subtitle")}
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
                    {/* LEFT — workspace */}
                    <div
                        className={`relative bg-white border rounded-sm overflow-hidden min-h-[480px] transition ${
                            isDragging && !sourceUrl
                                ? "border-[#059669] bg-[#059669]/[0.06]"
                                : "border-zinc-200"
                        }`}
                        onDragOver={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDropFile}
                    >
                        {!sourceUrl ? (
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploading}
                                data-testid="outline-blur-upload-btn"
                                className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 transition pointer-events-auto"
                            >
                                {uploading ? (
                                    <Loader2 className="h-8 w-8 animate-spin" />
                                ) : (
                                    <Upload className={`h-8 w-8 transition ${isDragging ? "text-[#059669] scale-110" : ""}`} />
                                )}
                                <span className="text-sm">
                                    {uploading
                                        ? t("tools.outline_blur.uploading")
                                        : isDragging
                                            ? t("tools.outline_blur.drop_release")
                                            : t("tools.outline_blur.click_or_drag")}
                                </span>
                                <span className="text-[11px] text-zinc-400">{t("tools.outline_blur.file_note")}</span>
                            </button>
                        ) : (
                            <div className="relative" data-testid="outline-blur-canvas-wrap">
                                {/* Bron foto — clicks fall through to canvas overlay above */}
                                <img
                                    ref={imgRef}
                                    src={sourceUrl}
                                    alt="Bron"
                                    onLoad={onImgLoad}
                                    crossOrigin="anonymous"
                                    className="w-full h-auto block select-none"
                                    draggable={false}
                                />
                                <canvas
                                    ref={canvasRef}
                                    onClick={onCanvasClick}
                                    data-testid="outline-blur-canvas"
                                    className="absolute inset-0 cursor-crosshair"
                                    style={{ touchAction: "none" }}
                                />
                                <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2 pointer-events-none">
                                    <span className="px-2 py-1 rounded-sm bg-black/70 text-[10px] uppercase tracking-wider text-zinc-300 backdrop-blur">
                                        {mode === "line" ? t("tools.outline_blur.mode_line") : t("tools.outline_blur.mode_blur")} · {t("tools.outline_blur.click_points")}
                                    </span>
                                    {draftPoints.length > 0 && (
                                        <button
                                            type="button"
                                            onClick={finishCurrent}
                                            data-testid="outline-blur-finish-shape"
                                            className="pointer-events-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[#E5A93D] text-black text-[11px] font-bold uppercase tracking-wider hover:bg-[#C8922C] transition"
                                        >
                                            <Check className="h-3.5 w-3.5" /> {t("tools.outline_blur.finish_shape")} ({draftPoints.length})
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={onPickFile}
                            className="hidden"
                            data-testid="outline-blur-file-input"
                        />
                    </div>

                    {/* RIGHT — controls */}
                    <div className="space-y-5">
                        {/* Mode toggle */}
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-2">{t("tools.outline_blur.mode")}</p>
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={() => { setMode("line"); setDraftPoints([]); }}
                                    data-testid="outline-blur-mode-line"
                                    className={`h-11 rounded-sm text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition ${
                                        mode === "line"
                                            ? "bg-[#E5A93D] text-black"
                                            : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                                    }`}
                                >
                                    <PenTool className="h-4 w-4" /> {t("tools.outline_blur.line")}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setMode("blur"); setDraftPoints([]); }}
                                    data-testid="outline-blur-mode-blur"
                                    className={`h-11 rounded-sm text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition ${
                                        mode === "blur"
                                            ? "bg-[#059669] text-white"
                                            : "bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-100"
                                    }`}
                                >
                                    <SquareIcon className="h-4 w-4" /> {t("tools.outline_blur.blur_zone")}
                                </button>
                            </div>
                        </div>

                        {/* Line settings */}
                        {mode === "line" && (
                            <div className="space-y-4 p-4 bg-white border border-zinc-200 rounded-sm">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">{t("tools.outline_blur.line_color")}</p>
                                        <code className="text-[11px] text-zinc-500 tabular-nums">{lineColor}</code>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={lineColor}
                                            onChange={(e) => setLineColor(e.target.value)}
                                            data-testid="outline-blur-color"
                                            className="h-10 w-12 bg-transparent rounded-sm border border-zinc-200 cursor-pointer"
                                        />
                                        <input
                                            type="text"
                                            value={lineColor}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                if (/^#([0-9a-f]{3}){1,2}$/i.test(v) || v.length <= 7) setLineColor(v);
                                            }}
                                            data-testid="outline-blur-color-hex"
                                            className="flex-1 h-10 bg-white border border-zinc-200 rounded-sm px-3 text-xs font-mono text-zinc-800 focus:outline-none focus:border-[#E5A93D]/60"
                                            placeholder="#RRGGBB"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">{t("tools.outline_blur.line_thickness")}</p>
                                        <code className="text-[11px] text-zinc-500 tabular-nums">{lineThickness} px</code>
                                    </div>
                                    <input
                                        type="range"
                                        min={1}
                                        max={120}
                                        value={lineThickness}
                                        onChange={(e) => setLineThickness(Number(e.target.value))}
                                        data-testid="outline-blur-thickness"
                                        className="w-full accent-[#E5A93D]"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Blur settings */}
                        {mode === "blur" && (
                            <div className="space-y-3 p-4 bg-white border border-zinc-200 rounded-sm">
                                <div className="flex items-center justify-between">
                                    <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold">{t("tools.outline_blur.blur_intensity")}</p>
                                    <code className="text-[11px] text-zinc-500 tabular-nums">{blurPercent}%</code>
                                </div>
                                <input
                                    type="range"
                                    min={1}
                                    max={100}
                                    value={blurPercent}
                                    onChange={(e) => setBlurPercent(Number(e.target.value))}
                                    data-testid="outline-blur-percent"
                                    className="w-full accent-[#059669]"
                                />
                                <p className="text-[11px] text-zinc-500 leading-relaxed">
                                    {t("tools.outline_blur.blur_tip_1")} <strong className="text-zinc-600">{t("tools.outline_blur.blur_tip_2")}</strong> {t("tools.outline_blur.blur_tip_3")}
                                </p>
                            </div>
                        )}

                        {/* Op list */}
                        {(operations.length > 0 || draftPoints.length > 0) && (
                            <div className="p-4 bg-white border border-zinc-200 rounded-sm">
                                <p className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 font-bold mb-2">
                                    {t("tools.outline_blur.operations")} ({operations.length}{draftPoints.length > 0 ? ` · ${t("tools.outline_blur.points_draft", { count: draftPoints.length })}` : ""})
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {operations.map((op, i) => (
                                        <span
                                            key={i}
                                            className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                                            style={{
                                                background: op.type === "line" ? (op.color || "#E5A93D") + "20" : "rgba(52, 211, 153, 0.15)",
                                                color: op.type === "line" ? (op.color || "#E5A93D") : "#059669",
                                                border: `1px solid ${op.type === "line" ? (op.color || "#E5A93D") + "60" : "rgba(52, 211, 153, 0.5)"}`,
                                            }}
                                        >
                                            {op.type === "line" ? t("tools.outline_blur.op_line", { thickness: op.thickness }) : t("tools.outline_blur.op_blur", { percent: op.blur_percent })}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={undo}
                                disabled={operations.length === 0 && draftPoints.length === 0}
                                data-testid="outline-blur-undo"
                                className="h-11 rounded-sm bg-white border border-zinc-200 text-zinc-600 text-xs font-bold uppercase tracking-wider hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition"
                            >
                                <Undo2 className="h-4 w-4" /> {t("tools.outline_blur.undo")}
                            </button>
                            <button
                                type="button"
                                onClick={clearAll}
                                disabled={operations.length === 0 && draftPoints.length === 0}
                                data-testid="outline-blur-clear"
                                className="h-11 rounded-sm bg-white border border-zinc-200 text-zinc-600 text-xs font-bold uppercase tracking-wider hover:bg-red-500/10 hover:border-red-500/30 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2 transition"
                            >
                                <Trash2 className="h-4 w-4" /> {t("tools.outline_blur.clear")}
                            </button>
                        </div>

                        {/* Download */}
                        <button
                            type="button"
                            onClick={submit}
                            disabled={submitting || !sourceUrl || (operations.length === 0 && draftPoints.length === 0)}
                            data-testid="outline-blur-download"
                            className="w-full h-12 rounded-sm bg-[#059669] hover:bg-[#047857] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-bold uppercase tracking-[0.14em] inline-flex items-center justify-center gap-2 transition"
                            style={{ boxShadow: "0 0 0 1px rgba(52, 211, 153, 0.5), 0 12px 28px -8px rgba(52, 211, 153, 0.6)" }}
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 animate-spin" /> {t("tools.outline_blur.rendering")}</>
                            ) : (
                                <><Download className="h-4 w-4" /> {t("tools.outline_blur.download_jpg")} {isAdmin ? "" : `(${t("tools.common.credit_1")})`}</>
                            )}
                        </button>

                        {resultUrl && (
                            <div className="p-4 bg-emerald-500/[0.06] border border-emerald-500/30 rounded-sm" data-testid="outline-blur-result">
                                <p className="text-[10px] uppercase tracking-[0.16em] text-emerald-700 font-bold mb-1">{t("tools.outline_blur.done")}</p>
                                <p className="text-xs text-zinc-600 mb-2">{t("tools.outline_blur.download_started")}</p>
                                <a
                                    href={resultUrl}
                                    download
                                    className="inline-flex items-center gap-2 text-xs text-emerald-700 hover:text-emerald-700 underline underline-offset-2"
                                >
                                    <Download className="h-3.5 w-3.5" /> {t("tools.outline_blur.download_again")}
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
